import { useEffect, useState } from "react";
import { Package, Search, Save, Trash2, Plus, X, ChevronDown, ChevronUp, Edit3, Wand2, Upload, Layers, AlertTriangle, Cloud, GripVertical } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import usePageTitle from "@/hooks/use-page-title";
import { useAuth } from "@/context/AuthContext";

const emptyProduct = {
  group: "Business Cards",
  category: "",
  groups: [],
  categories: [],
  name: "",
  slug: "",
  subtitle: "",
  description: "",
  features: [],
  base_price: 0,
  mrp: 0,
  discount_pct: 0,
  unit: "per piece",
  min_order: 1,
  min_quantity: 1,
  max_quantity: 0,
  quantity_step: 1,
  quantity_tiers: [],
  options: [],
  production_time_options: [],
  faqs: [],
  tags: [],
  display_order: 0,
  active: true,
  trending: false,
  best_seller: false,
  image_url: "",
  images: [],
  is_ecommerce: true,
  stock: -1,
  sku: "",
  design_pages: 1,
  design_charge: 0,
  gst_rate: 18,
  hsn_code: "4911",
};

export default function Catalog() {
  usePageTitle("Catalog");
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [group, setGroup] = useState("");
  const [groups, setGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null); // full product object being edited
  const [showEditor, setShowEditor] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [taxOpen, setTaxOpen] = useState(false);
  const [wipeOpen, setWipeOpen] = useState(false);
  const [storageOpen, setStorageOpen] = useState(false);

  const load = async () => {
    try {
      setRows((await api.get("/catalog")).data);
      setGroups((await api.get("/catalog/groups")).data);
      setCategories((await api.get("/catalog/categories")).data);
    } catch { toast.error("Could not load catalog"); }
  };

  useEffect(() => { load(); }, []);

  const filtered = rows.filter((r) =>
    (!group || r.group === group) &&
    (!q || `${r.name} ${r.category} ${r.group}`.toLowerCase().includes(q.toLowerCase()))
  );

  const openEditor = (product) => {
    setEditing(product ? JSON.parse(JSON.stringify(product)) : { ...emptyProduct });
    setShowEditor(true);
  };

  const saveProduct = async () => {
    if (!editing.name?.trim()) { toast.error("Name required"); return; }
    if (!editing.group) { toast.error("Group required"); return; }
    // Clean payload
    const payload = { ...editing };
    delete payload.id;
    delete payload.created_at;
    delete payload._id;
    try {
      if (editing.id) {
        await api.put(`/catalog/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/catalog", payload);
        toast.success("Product created");
      }
      setShowEditor(false); setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/catalog/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const seed = async () => {
    if (!window.confirm("Seed the sample catalog? Existing products (by name) will be skipped.")) return;
    try {
      const { data } = await api.post("/catalog/seed");
      toast.success(`Seeded ${data.created} new products`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Seed failed"); }
  };

  const reseed = async () => {
    if (!window.confirm("⚠️ WIPE and reseed the catalog? This will delete ALL current products and carts.")) return;
    try {
      const { data } = await api.post("/catalog/reseed");
      toast.success(`Reseeded ${data.created} products`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reseed failed"); }
  };

  const applyStockImages = async () => {
    if (!window.confirm("Apply curated stock images to any product missing images?")) return;
    try {
      const { data } = await api.post("/catalog/apply-stock-images");
      toast.success(`Updated ${data.updated} products`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const quickToggle = async (p, field) => {
    try {
      await api.put(`/catalog/${p.id}`, { ...p, [field]: !p[field] });
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Package className="text-[#FF6B00]" />
          <h1 className="text-2xl font-black tracking-tight">Product Catalog</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setTaxOpen(true)} className="btn-swiss btn-outline text-xs" data-testid="catalog-manage-taxonomy-btn"><Layers size={12} /> Groups & Categories</button>
          {isSuperAdmin && (
            <button onClick={() => setStorageOpen(true)} className="btn-swiss btn-outline text-xs text-sky-700 border-sky-300 hover:bg-sky-50" data-testid="storage-status-btn" title="View storage status and migrate legacy files">
              <Cloud size={12} /> Storage
            </button>
          )}
          {isSuperAdmin && (
            <button onClick={() => setWipeOpen(true)} className="btn-swiss btn-outline text-xs text-red-600 border-red-300 hover:bg-red-50" data-testid="danger-wipe-btn" title="Wipe all orders, customers, payments — clean start for launch">
              <AlertTriangle size={12} /> Fresh Start
            </button>
          )}
          <button onClick={applyStockImages} className="btn-swiss btn-outline text-xs" data-testid="catalog-stock-images-btn">Auto Stock Images</button>
          <button onClick={seed} className="btn-swiss btn-outline text-xs" data-testid="catalog-seed-btn">Seed Sample</button>
          <button onClick={reseed} className="btn-swiss btn-outline text-xs text-red-600" data-testid="catalog-reseed-btn">Wipe & Reseed</button>
          <button onClick={() => openEditor(null)} className="btn-swiss btn-accent text-xs" data-testid="catalog-new-btn">
            <Plus size={12} /> New Product
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="flex-1 min-w-[240px] flex items-center border-2 border-zinc-200 rounded-lg px-3 py-2 bg-white">
          <Search size={14} className="text-zinc-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…"
            className="flex-1 px-2 bg-transparent outline-none text-sm" data-testid="catalog-search" />
        </div>
        <select value={group} onChange={(e) => setGroup(e.target.value)} className="input max-w-xs" data-testid="catalog-group-filter">
          <option value="">All groups</option>
          {groups.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
      </div>

      <div className="border-2 border-zinc-200 rounded-2xl bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3">Product</th>
                <th className="text-left px-4 py-3">Group / Category</th>
                <th className="text-left px-4 py-3">Base</th>
                <th className="text-left px-4 py-3">Tiers</th>
                <th className="text-left px-4 py-3">Options</th>
                <th className="text-center px-4 py-3">Active</th>
                <th className="text-center px-4 py-3">Sellable</th>
                <th className="text-center px-4 py-3">Trending</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="catalog-rows">
              {filtered.map((p) => (
                <tr key={p.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-3">
                    <div className="font-bold">{p.name}</div>
                    {p.subtitle && <div className="text-xs text-zinc-500 line-clamp-1 max-w-xs">{p.subtitle}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs font-semibold">{p.group}</div>
                    <div className="text-xs text-zinc-500">{p.category}</div>
                  </td>
                  <td className="px-4 py-3 font-mono">
                    ₹{p.base_price} <span className="text-[10px] text-zinc-500">{p.unit}</span>
                    <span className="ml-2 text-[9px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded uppercase tracking-wider" data-testid={`cat-row-gst-${p.id}`}>
                      GST {(p.gst_rate ?? 18) === 0 ? "Nil" : `${p.gst_rate ?? 18}%`}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs">{p.quantity_tiers?.length || 0}</td>
                  <td className="px-4 py-3 text-xs">{p.options?.length || 0}</td>
                  <td className="px-4 py-3 text-center"><Toggle val={p.active} onClick={() => quickToggle(p, "active")} data-testid={`active-${p.id}`} /></td>
                  <td className="px-4 py-3 text-center"><Toggle val={p.is_ecommerce} onClick={() => quickToggle(p, "is_ecommerce")} accent="orange" /></td>
                  <td className="px-4 py-3 text-center"><Toggle val={p.trending} onClick={() => quickToggle(p, "trending")} accent="blue" /></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openEditor(p)} className="text-xs font-bold text-[#FF6B00] mr-2" data-testid={`edit-${p.id}`}>
                      <Edit3 size={12} className="inline mr-1" />Edit
                    </button>
                    <button onClick={() => deleteProduct(p.id)} className="text-red-500 hover:text-red-700" data-testid={`del-${p.id}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="text-center py-10 text-zinc-500">No products.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showEditor && editing && (
        <ProductEditor
          product={editing}
          setProduct={setEditing}
          groups={groups}
          categories={categories}
          onTaxonomyChange={load}
          onClose={() => { setShowEditor(false); setEditing(null); }}
          onSave={saveProduct}
        />
      )}
      {taxOpen && <TaxonomyModal onClose={() => { setTaxOpen(false); load(); }} />}
      {wipeOpen && <DangerWipeModal onClose={() => { setWipeOpen(false); load(); }} />}
      {storageOpen && <StorageModal onClose={() => setStorageOpen(false)} />}
    </div>
  );
}

function DangerWipeModal({ onClose }) {
  const [scope, setScope] = useState("all"); // "all" | "shop_only"
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const CONFIRM_TOKENS = { all: "YES-WIPE-ALL", shop_only: "YES-WIPE-SHOP" };
  const CONFIRM = CONFIRM_TOKENS[scope];
  const canWipe = confirmText === CONFIRM;

  // Reset confirm input when scope changes so user can't accidentally submit stale text
  useEffect(() => { setConfirmText(""); }, [scope]);

  const doWipe = async () => {
    if (!canWipe) return;
    setBusy(true);
    try {
      const url = scope === "shop_only"
        ? `/admin/danger/wipe-shop-orders?confirm=${CONFIRM}`
        : `/admin/danger/wipe-transactional?confirm=${CONFIRM}`;
      const { data } = await api.post(url);
      const total = Object.values(data.wiped || {}).reduce((s, n) => s + (n || 0), 0);
      toast.success(`Wiped ${total} records ${scope === "shop_only" ? "(online orders only)" : "— clean start ready"} 🎉`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wipe failed");
    } finally { setBusy(false); }
  };

  const isShopOnly = scope === "shop_only";

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="danger-wipe-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden ring-2 ring-red-500" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-red-200 bg-red-50">
          <div className="flex items-center gap-2">
            <AlertTriangle size={20} className="text-red-600" />
            <div>
              <div className="overline text-red-600">Danger Zone</div>
              <div className="font-display text-xl font-black tracking-tighter text-red-800">Fresh Start Wipe</div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-red-100 rounded-full" data-testid="danger-close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          {/* Scope picker */}
          <div className="grid grid-cols-2 gap-2" data-testid="danger-scope-picker">
            <button
              onClick={() => setScope("shop_only")}
              className={`p-3 rounded-xl border-2 text-left transition ${isShopOnly ? "border-amber-500 bg-amber-50" : "border-zinc-200 hover:border-zinc-300"}`}
              data-testid="danger-scope-shop"
            >
              <div className="text-xs font-black uppercase tracking-wider text-amber-700">Online orders only</div>
              <div className="text-[11px] text-zinc-600 mt-1">Wipe shop orders + carts + web-bridged production orders. Keeps offline work.</div>
            </button>
            <button
              onClick={() => setScope("all")}
              className={`p-3 rounded-xl border-2 text-left transition ${!isShopOnly ? "border-red-500 bg-red-50" : "border-zinc-200 hover:border-zinc-300"}`}
              data-testid="danger-scope-all"
            >
              <div className="text-xs font-black uppercase tracking-wider text-red-700">Everything transactional</div>
              <div className="text-[11px] text-zinc-600 mt-1">All orders, customers, payments, quotations, uploads, notifications.</div>
            </button>
          </div>

          {isShopOnly ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 space-y-1">
              <div>✅ <b>Kept:</b> Catalog, customers, offline orders, offline payments, staff, settings</div>
              <div>❌ <b>Deleted:</b> All `shop_orders`, `payment_intents`, `carts`, web-bridged orders, shop notifications</div>
            </div>
          ) : (
            <>
              <p className="text-sm text-zinc-700">
                This will <b>permanently delete ALL</b> orders, shop orders, customers, payments, transactions, quotations, invoice counters, notifications, uploaded designs, carts and saved addresses.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 space-y-1">
                <div>✅ <b>Kept:</b> Product catalog, staff accounts, business settings, notification recipients</div>
                <div>❌ <b>Deleted:</b> Every order, customer login &amp; their data, payment history</div>
              </div>
            </>
          )}

          <div>
            <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500 mb-1">Type <span className="font-mono text-red-600">{CONFIRM}</span> to confirm</label>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={CONFIRM}
              className={`w-full border-2 rounded-xl px-3 py-2 text-sm font-mono outline-none ${canWipe ? "border-red-500 focus:border-red-600 text-red-700" : "border-zinc-200 focus:border-red-400"}`}
              data-testid="danger-confirm-input"
            />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-zinc-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn-swiss btn-outline text-xs" data-testid="danger-cancel">Cancel</button>
          <button
            onClick={doWipe}
            disabled={!canWipe || busy}
            className={`inline-flex items-center gap-2 text-white font-bold px-5 py-2 rounded-full text-xs disabled:opacity-40 disabled:cursor-not-allowed ${isShopOnly ? "bg-amber-600 hover:bg-amber-700" : "bg-red-600 hover:bg-red-700"}`}
            data-testid="danger-wipe-confirm-btn"
          >
            <Trash2 size={12} /> {busy ? "Wiping…" : (isShopOnly ? "Wipe Online Orders" : "Wipe Everything")}
          </button>
        </div>
      </div>
    </div>
  );
}

function TaxonomyModal({ onClose }) {
  const [data, setData] = useState({ groups: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [headerGroups, setHeaderGroups] = useState([]); // visible groups (ordered)
  const [headerSaving, setHeaderSaving] = useState(false);
  const [orderSaving, setOrderSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(-1);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/catalog/taxonomy");
      setData(data);
      const hg = await api.get("/settings/header-groups");
      setHeaderGroups(Array.isArray(hg.data?.groups) ? hg.data.groups : []);
    } catch { toast.error("Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Persist the current group order to the backend (also used by /catalog/groups and public header)
  const persistGroupOrder = async (nextGroups) => {
    setOrderSaving(true);
    try {
      await api.put("/admin/catalog/groups/order", { order: nextGroups.map((g) => g.name) });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save order failed");
      // Rollback by reloading server state
      load();
      return;
    } finally { setOrderSaving(false); }
  };

  // HTML5 drag-and-drop handlers for the Groups block
  const handleDragStart = (idx) => (e) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
    // Firefox needs some data set for drag to start
    try { e.dataTransfer.setData("text/plain", String(idx)); } catch { /* noop */ }
  };
  const handleDragOver = (idx) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const handleDrop = (idx) => (e) => {
    e.preventDefault();
    const from = dragIdx;
    setDragIdx(-1);
    if (from < 0 || from === idx) return;
    setData((prev) => {
      const next = [...(prev.groups || [])];
      const [m] = next.splice(from, 1);
      next.splice(idx, 0, m);
      // Fire-and-forget persist; state updates immediately for a snappy UX
      persistGroupOrder(next);
      return { ...prev, groups: next };
    });
  };
  const handleDragEnd = () => setDragIdx(-1);

  const toggleHeaderGroup = (name) => {
    setHeaderGroups((prev) =>
      prev.includes(name) ? prev.filter((g) => g !== name) : [...prev, name]
    );
  };

  const moveHeaderGroup = (name, dir) => {
    setHeaderGroups((prev) => {
      const idx = prev.indexOf(name);
      if (idx < 0) return prev;
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const saveHeaderGroups = async () => {
    setHeaderSaving(true);
    try {
      await api.put("/admin/settings/header-groups", { groups: headerGroups });
      toast.success("Header groups saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setHeaderSaving(false); }
  };

  const rename = async (kind, oldName) => {
    const newName = window.prompt(`Rename "${oldName}" to:`, oldName);
    if (newName === null || newName.trim() === oldName) return;
    setBusy(oldName);
    try {
      await api.put(`/admin/catalog/${kind}`, { old_name: oldName, new_name: newName.trim() });
      toast.success(newName.trim() ? `Renamed to "${newName.trim()}"` : "Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
    finally { setBusy(""); }
  };
  const remove = async (kind, name, count) => {
    if (count > 0) { toast.error(`${count} product(s) still use "${name}". Rename them first.`); return; }
    if (!window.confirm(`Delete "${name}"?`)) return;
    setBusy(name);
    try {
      await api.put(`/admin/catalog/${kind}`, { old_name: name, new_name: "" });
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(""); }
  };
  const createNew = async (kind, name) => {
    const clean = (name || "").trim();
    if (!clean) return;
    setBusy(`__create_${kind}`);
    try {
      await api.post(`/admin/catalog/${kind}`, { new_name: clean });
      toast.success(`${kind === "groups" ? "Group" : "Category"} "${clean}" added`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setBusy(""); }
  };

  const Block = ({ title, kind, items }) => {
    const [newName, setNewName] = useState("");
    const submit = async (e) => {
      e?.preventDefault?.();
      await createNew(kind, newName);
      setNewName("");
    };
    return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="font-display font-black tracking-tight text-lg">{title}</div>
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">{items.length} in use</div>
      </div>
      <form onSubmit={submit} className="flex gap-2 mb-2" data-testid={`tax-add-${kind}-form`}>
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder={`+ Add new ${title.toLowerCase().slice(0, -1)}`}
          maxLength={60}
          className="flex-1 border-2 border-zinc-200 focus:border-[#FF6B00] rounded-full px-3 py-2 text-xs outline-none"
          data-testid={`tax-add-${kind}-input`}
        />
        <button
          type="submit"
          disabled={!newName.trim() || busy === `__create_${kind}`}
          className="inline-flex items-center gap-1 bg-[#FF6B00] hover:bg-[#e65a00] disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-bold px-4 py-2 rounded-full"
          data-testid={`tax-add-${kind}-btn`}
        >
          <Plus size={12} /> Add
        </button>
      </form>
      <div className="border-2 border-zinc-200 rounded-xl overflow-hidden bg-white" data-testid={`tax-list-${kind}`}>
        {items.length === 0 && <div className="text-xs text-zinc-500 p-4 text-center">None yet — use the input above to create your first {title.toLowerCase().slice(0, -1)}.</div>}
        {items.map((it, idx) => {
          const isGroups = kind === "groups";
          const dragging = isGroups && dragIdx === idx;
          return (
          <div
            key={it.name}
            draggable={isGroups}
            onDragStart={isGroups ? handleDragStart(idx) : undefined}
            onDragOver={isGroups ? handleDragOver(idx) : undefined}
            onDrop={isGroups ? handleDrop(idx) : undefined}
            onDragEnd={isGroups ? handleDragEnd : undefined}
            className={`flex items-center justify-between px-3 py-2 border-b border-zinc-100 last:border-0 hover:bg-orange-50 ${dragging ? "opacity-30 bg-orange-100" : ""} ${isGroups ? "cursor-move" : ""}`}
            data-testid={`tax-row-${kind}-${it.name.replace(/\s/g, "-")}`}
          >
            <div className="flex items-center gap-2">
              {isGroups && (
                <GripVertical size={14} className="text-zinc-300 hover:text-[#FF6B00] shrink-0" aria-hidden />
              )}
              <span className="text-sm font-semibold">{it.name}</span>
              <span className={`text-[10px] font-mono ${it.count === 0 ? "text-amber-600" : "text-zinc-500"}`}>
                {it.count === 0 ? "(empty)" : `(${it.count} product${it.count === 1 ? "" : "s"})`}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => rename(kind, it.name)} disabled={busy === it.name}
                className="p-1.5 text-zinc-500 hover:text-[#FF6B00] disabled:opacity-40" title="Rename"
                data-testid={`tax-rename-${kind}-${it.name.replace(/\s/g, "-")}`}>
                <Edit3 size={12} />
              </button>
              <button onClick={() => remove(kind, it.name, it.count)} disabled={busy === it.name || it.count > 0}
                className="p-1.5 text-zinc-400 hover:text-red-500 disabled:opacity-40" title={it.count > 0 ? `${it.count} product(s) use this — rename them first` : "Delete"}
                data-testid={`tax-delete-${kind}-${it.name.replace(/\s/g, "-")}`}>
                <Trash2 size={12} />
              </button>
            </div>
          </div>
          );
        })}
      </div>
    </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="tax-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
          <div>
            <div className="overline text-[#FF6B00]">Catalog Taxonomy</div>
            <div className="font-display text-xl font-black tracking-tighter">Groups & Categories</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-full" data-testid="tax-close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-6 overflow-y-auto">
          {loading ? (
            <div className="text-center text-sm text-zinc-500 py-6">Loading…</div>
          ) : (
            <>
              {/* Header visibility panel */}
              <div className="border-2 border-orange-200 bg-orange-50/40 rounded-2xl p-4" data-testid="header-groups-panel">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-display font-black tracking-tight text-lg">Show in Public Header</div>
                    <div className="text-[11px] text-zinc-500">Tick + reorder the groups shown in the storefront navigation strip.</div>
                  </div>
                  <button
                    onClick={saveHeaderGroups}
                    disabled={headerSaving}
                    className="btn-swiss btn-accent text-xs disabled:opacity-50"
                    data-testid="header-groups-save"
                  >
                    {headerSaving ? "Saving…" : "Save Header"}
                  </button>
                </div>
                <div className="space-y-1.5">
                  {data.groups.map((it) => {
                    const visible = headerGroups.includes(it.name);
                    const idx = headerGroups.indexOf(it.name);
                    return (
                      <div key={it.name} className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${visible ? "border-[#FF6B00] bg-white" : "border-zinc-200 bg-zinc-50"}`} data-testid={`hg-row-${it.name.replace(/\s/g, "-")}`}>
                        <input
                          type="checkbox"
                          checked={visible}
                          onChange={() => toggleHeaderGroup(it.name)}
                          className="w-4 h-4 accent-[#FF6B00]"
                          data-testid={`hg-toggle-${it.name.replace(/\s/g, "-")}`}
                        />
                        <span className={`text-sm font-semibold flex-1 ${visible ? "text-zinc-900" : "text-zinc-400"}`}>{it.name}</span>
                        <span className="text-[10px] text-zinc-500 font-mono">{it.count}</span>
                        {visible && (
                          <div className="flex items-center gap-1">
                            <button onClick={() => moveHeaderGroup(it.name, -1)} disabled={idx <= 0} className="p-1 text-zinc-500 hover:text-[#FF6B00] disabled:opacity-30" title="Move up" data-testid={`hg-up-${it.name.replace(/\s/g, "-")}`}>
                              <ChevronUp size={12} />
                            </button>
                            <button onClick={() => moveHeaderGroup(it.name, 1)} disabled={idx === headerGroups.length - 1} className="p-1 text-zinc-500 hover:text-[#FF6B00] disabled:opacity-30" title="Move down" data-testid={`hg-down-${it.name.replace(/\s/g, "-")}`}>
                              <ChevronDown size={12} />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[11px] text-zinc-500 flex items-center gap-1">
                    <GripVertical size={12} className="text-zinc-400" />
                    <span>Drag rows to reorder — the new order applies to the storefront header, product filters and the group picker.</span>
                  </div>
                  {orderSaving && <span className="text-[10px] text-[#FF6B00] font-mono">Saving…</span>}
                </div>
                <Block title="Groups" kind="groups" items={data.groups} />
              </div>
              <Block title="Categories" kind="categories" items={data.categories} />
              <div className="text-[11px] text-zinc-500 bg-amber-50 border border-amber-200 rounded-xl p-3">
                <b>💡 Rename</b> instantly updates every product using that group / category. <b>Delete</b> is allowed only when no products reference the value — rename the products first.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Toggle({ val, onClick, accent = "emerald", ...rest }) {
  const on = val ? (accent === "orange" ? "bg-[#FF6B00]" : accent === "blue" ? "bg-blue-500" : "bg-emerald-500") : "bg-zinc-300";
  return (
    <button onClick={onClick} className={`w-9 h-5 rounded-full transition-colors ${on} relative`} {...rest}>
      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${val ? "left-4" : "left-0.5"}`} />
    </button>
  );
}

function ProductEditor({ product, setProduct, groups, categories = [], onTaxonomyChange, onClose, onSave }) {
  const set = (k, v) => setProduct({ ...product, [k]: v });
  const [tab, setTab] = useState("basic");
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-3xl bg-white h-full overflow-y-auto p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="product-editor">
        <div className="flex items-center justify-between mb-4 sticky top-0 bg-white z-10 pb-4 border-b border-zinc-100">
          <div>
            <div className="overline text-[#FF6B00]">{product.id ? "Edit" : "Create"}</div>
            <div className="font-display text-2xl font-black tracking-tight">{product.name || "New Product"}</div>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-swiss btn-outline text-xs">Cancel</button>
            <button onClick={onSave} className="btn-swiss btn-accent text-xs" data-testid="editor-save"><Save size={12} /> Save</button>
          </div>
        </div>

        <div className="flex gap-2 mb-5 flex-wrap">
          {["basic", "images", "tiers", "options", "faqs", "meta"].map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-full ${tab === t ? "bg-[#FF6B00] text-white" : "border-2 border-zinc-200 hover:border-[#FF6B00]"}`}
              data-testid={`tab-${t}`}
            >{t}</button>
          ))}
        </div>

        {tab === "basic" && (
          <div className="space-y-4">
            <Field label="Name*"><input className="input" value={product.name} onChange={(e) => set("name", e.target.value)} data-testid="edit-name" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Group*">
                <TaxonomyPicker
                  kind="groups"
                  value={product.group}
                  options={groups}
                  onChange={(v) => set("group", v)}
                  onCreated={onTaxonomyChange}
                  testIdPrefix="edit-group"
                />
              </Field>
              <Field label="Category">
                <TaxonomyPicker
                  kind="categories"
                  value={product.category}
                  options={categories}
                  onChange={(v) => set("category", v)}
                  onCreated={onTaxonomyChange}
                  testIdPrefix="edit-category"
                />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3 bg-blue-50/40 border-2 border-blue-100 rounded-2xl p-3">
              <Field label="Additional Groups (product also appears in these)">
                <MultiTaxonomyPicker
                  value={product.groups || []}
                  primary={product.group}
                  options={groups}
                  onChange={(v) => set("groups", v)}
                  testIdPrefix="edit-groups-extra"
                />
              </Field>
              <Field label="Additional Categories (product also appears in these)">
                <MultiTaxonomyPicker
                  value={product.categories || []}
                  primary={product.category}
                  options={categories}
                  onChange={(v) => set("categories", v)}
                  testIdPrefix="edit-categories-extra"
                />
              </Field>
              <div className="col-span-2 text-[10px] text-zinc-500 -mt-1">
                A product can belong to multiple groups &amp; categories. The <b>primary</b> group/category above is used as the default label on cards; additional values widen where the product shows up in the storefront filters and mega-menu.
              </div>
            </div>
            <Field label="Subtitle">
              <input className="input" value={product.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} placeholder="Short marketing line" />
            </Field>
            <Field label="Description">
              <textarea className="input min-h-[100px]" value={product.description || ""} onChange={(e) => set("description", e.target.value)} />
            </Field>
            <Field label="Features (one per line)">
              <textarea className="input min-h-[80px]" value={(product.features || []).join("\n")}
                onChange={(e) => set("features", e.target.value.split("\n").filter(Boolean))} />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Base Price ₹"><input type="number" className="input" value={product.base_price} onChange={(e) => set("base_price", parseFloat(e.target.value) || 0)} /></Field>
              <Field label="Unit"><input className="input" value={product.unit} onChange={(e) => set("unit", e.target.value)} /></Field>
              <Field label="Image URL"><input className="input" value={product.image_url || ""} onChange={(e) => set("image_url", e.target.value)} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3 bg-orange-50/40 border-2 border-orange-100 rounded-2xl p-3">
              <Field label="MRP ₹ (list price — shown struck)">
                <input type="number" min="0" step="0.01" className="input" value={product.mrp || 0} onChange={(e) => set("mrp", parseFloat(e.target.value) || 0)} data-testid="cat-mrp" />
              </Field>
              <Field label="Discount % (used if MRP is blank)">
                <input type="number" min="0" max="99" className="input" value={product.discount_pct || 0} onChange={(e) => set("discount_pct", Math.min(99, Math.max(0, parseFloat(e.target.value) || 0)))} data-testid="cat-discount-pct" />
              </Field>
              <div className="col-span-2 text-[10px] text-zinc-500 -mt-1">
                Fill <b>MRP</b> (recommended) OR <b>Discount %</b> — customer sees struck-through MRP + %OFF badge on cards, PDP and cart. Purely visual, doesn't change what they pay.
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Min Qty"><input type="number" className="input" value={product.min_quantity || 1} onChange={(e) => set("min_quantity", parseInt(e.target.value) || 1)} /></Field>
              <Field label="Max Qty (0=unlimited)"><input type="number" className="input" value={product.max_quantity || 0} onChange={(e) => set("max_quantity", parseInt(e.target.value) || 0)} /></Field>
              <Field label="Qty Step"><input type="number" className="input" value={product.quantity_step || 1} onChange={(e) => set("quantity_step", parseInt(e.target.value) || 1)} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3 bg-emerald-50/40 border-2 border-emerald-100 rounded-2xl p-3">
              <Field label="Design Pages (used when Design Charge is auto)">
                <input type="number" min="1" className="input" value={product.design_pages || 1} onChange={(e) => set("design_pages", Math.max(1, parseInt(e.target.value) || 1))} data-testid="cat-design-pages" />
              </Field>
              <Field label="Design Charge ₹ (flat, when customer picks 'Design for me')">
                <input
                  type="number"
                  min="0"
                  step="1"
                  className="input"
                  value={product.design_charge || 0}
                  onChange={(e) => set("design_charge", Math.max(0, parseFloat(e.target.value) || 0))}
                  placeholder={`auto: ${499 * (product.design_pages || 1)}`}
                  data-testid="cat-design-charge"
                />
                <p className="text-[10px] text-zinc-500 mt-1">Leave 0 to auto-calculate (₹499 × design pages).</p>
              </Field>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <Field label="GST % (prices below are treated as INCLUSIVE of this rate)">
                <div className="flex gap-1">
                  {[0, 5, 12, 18, 28].map((r) => {
                    const sel = Number(product.gst_rate ?? 18) === r;
                    return (
                      <button
                        key={r}
                        type="button"
                        onClick={() => set("gst_rate", r)}
                        className={`flex-1 px-2 py-2 text-xs font-bold border-2 rounded-lg transition-colors ${sel ? "border-[#FF6B00] bg-orange-50 text-[#FF6B00]" : "border-zinc-200 hover:border-zinc-400 text-zinc-600"}`}
                        data-testid={`cat-gst-${r}`}
                      >
                        {r === 0 ? "Nil" : `${r}%`}
                      </button>
                    );
                  })}
                </div>
                <div className="text-[10px] text-zinc-500 mt-1">
                  Custom: <input type="number" min="0" max="100" step="0.1" value={product.gst_rate ?? 18} onChange={(e) => set("gst_rate", Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)))} className="input inline-block w-20 py-1 text-xs" data-testid="cat-gst-custom" /> %
                </div>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="HSN / SAC Code (mandatory for GST invoice > ₹5Cr turnover)">
                <input
                  className="input font-mono"
                  value={product.hsn_code ?? "4911"}
                  onChange={(e) => set("hsn_code", e.target.value.replace(/[^0-9]/g, "").slice(0, 8))}
                  placeholder="4911"
                  data-testid="cat-hsn-code"
                />
                <div className="text-[10px] text-zinc-500 mt-1">
                  Common: <button type="button" onClick={() => set("hsn_code","4911")} className="underline hover:text-[#FF6B00]">4911</button> printed matter · <button type="button" onClick={() => set("hsn_code","4820")} className="underline hover:text-[#FF6B00]">4820</button> registers/books · <button type="button" onClick={() => set("hsn_code","4817")} className="underline hover:text-[#FF6B00]">4817</button> envelopes · <button type="button" onClick={() => set("hsn_code","9989")} className="underline hover:text-[#FF6B00]">9989</button> printing services · <button type="button" onClick={() => set("hsn_code","3919")} className="underline hover:text-[#FF6B00]">3919</button> self-adhesive vinyl
                </div>
              </Field>
              <div className="text-[10px] text-zinc-500 pt-6">
                <span className="font-bold">Why HSN?</span> Every taxable invoice must show HSN (goods) or SAC (services) codes. Mandatory above ₹5Cr annual turnover; recommended always for GST filing.
              </div>
            </div>
            <div className="text-[10px] text-zinc-500 -mt-2">
              <span className="font-bold">Tip:</span> Bi-Fold = 2 pages, Tri-Fold = 3 pages. Standard print = 18% GST · Books & greeting cards = 5% · Some novelty gifts = 12% or 28%.
            </div>
          </div>
        )}

        {tab === "tiers" && (
          <TiersEditor
            tiers={product.quantity_tiers || []}
            setTiers={(v) => set("quantity_tiers", v)}
          />
        )}

        {tab === "images" && (
          <ImagesEditor
            imageUrl={product.image_url || ""}
            setImageUrl={(v) => set("image_url", v)}
            images={product.images || []}
            setImages={(v) => set("images", v)}
            productId={product.id}
          />
        )}

        {tab === "options" && (
          <OptionsEditor
            options={product.options || []}
            setOptions={(v) => set("options", v)}
          />
        )}

        {tab === "faqs" && (
          <FAQEditor faqs={product.faqs || []} setFaqs={(v) => set("faqs", v)} />
        )}

        {tab === "meta" && (
          <div className="space-y-4">
            <Field label="Tags (comma separated)">
              <input className="input" value={(product.tags || []).join(", ")} onChange={(e) => set("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))} />
            </Field>
            <Field label="Slug (URL)"><input className="input" value={product.slug || ""} onChange={(e) => set("slug", e.target.value)} placeholder="Auto from name if blank" /></Field>
            <Field label="SKU"><input className="input" value={product.sku || ""} onChange={(e) => set("sku", e.target.value)} /></Field>
            <Field label="Stock (-1 = unlimited)"><input type="number" className="input" value={product.stock ?? -1} onChange={(e) => set("stock", parseInt(e.target.value))} /></Field>
            <Field label="Display Order"><input type="number" className="input" value={product.display_order || 0} onChange={(e) => set("display_order", parseInt(e.target.value) || 0)} /></Field>
            <div className="grid grid-cols-2 gap-3 pt-2">
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!product.active} onChange={(e) => set("active", e.target.checked)} /> Active</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!product.is_ecommerce} onChange={(e) => set("is_ecommerce", e.target.checked)} /> Sellable Online</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!product.trending} onChange={(e) => set("trending", e.target.checked)} /> Trending</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!product.best_seller} onChange={(e) => set("best_seller", e.target.checked)} /> Best Seller</label>
            </div>
            <Field label="Production Time Options (one per line)">
              <textarea className="input min-h-[60px]" value={(product.production_time_options || []).join("\n")} onChange={(e) => set("production_time_options", e.target.value.split("\n").filter(Boolean))} />
            </Field>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

function ImagesEditor({ imageUrl, setImageUrl, images, setImages, productId }) {
  const [newUrl, setNewUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  const add = () => {
    const u = newUrl.trim();
    if (!u) return;
    if (!images.includes(u)) setImages([...images, u]);
    if (!imageUrl) setImageUrl(u);
    setNewUrl("");
  };
  const remove = (i) => {
    const next = images.filter((_, idx) => idx !== i);
    setImages(next);
    if (imageUrl === images[i]) setImageUrl(next[0] || "");
  };
  const setPrimary = (u) => setImageUrl(u);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!productId) {
      toast.error("Save the product first — then upload images.");
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be < 5MB"); return; }
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Only JPEG/PNG/WEBP supported"); return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/catalog/${productId}/upload-image`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Build a full URL from the returned relative path if backend didn't include an origin
      const fullUrl = (data.url || "").startsWith("http")
        ? data.url
        : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      const next = images.includes(fullUrl) ? images : [...images, fullUrl];
      setImages(next);
      if (!imageUrl) setImageUrl(fullUrl);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-5">
      <div className="text-sm text-zinc-600">
        Upload your own images or paste URLs to build a product gallery. The first image (or the one you mark as primary)
        is used on listings, cart and PDP hero. Recommended: 1200×900+, 16:9 or 4:3.
      </div>

      {/* Upload */}
      <div className="p-4 border-2 border-dashed border-zinc-300 hover:border-[#FF6B00] rounded-xl bg-zinc-50/50">
        <label className="cursor-pointer block text-center">
          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleUpload} className="hidden" disabled={uploading} data-testid="img-upload-input" />
          <div className="flex flex-col items-center gap-2 py-2">
            <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
              <Upload size={20} className="text-[#FF6B00]" />
            </div>
            <div className="font-bold text-sm">{uploading ? "Uploading…" : "Upload from your computer"}</div>
            <div className="text-[10px] text-zinc-500">JPEG · PNG · WEBP · max 5 MB</div>
          </div>
        </label>
        {!productId && (
          <div className="mt-2 text-[11px] text-amber-700 text-center">Save the product first, then upload images.</div>
        )}
      </div>

      {/* Add via URL */}
      <div className="space-y-2">
        <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500">Or paste an Image URL</label>
        <div className="flex gap-2">
          <input
            className="input flex-1"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            placeholder="https://images.unsplash.com/photo-..."
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
            data-testid="img-url-input"
          />
          <button onClick={add} className="btn-swiss btn-accent text-xs" data-testid="img-url-add"><Plus size={12} /> Add</button>
        </div>
      </div>

      {/* Current gallery */}
      {images.length === 0 && (
        <div className="p-6 border-2 border-dashed border-zinc-200 rounded-xl text-center text-sm text-zinc-500">
          No images yet. Upload above, add a URL, or use "Wipe & Reseed" to auto-populate curated stock imagery.
        </div>
      )}

      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-3" data-testid="images-gallery">
          {images.map((u, i) => {
            const isPrimary = u === imageUrl;
            return (
              <div key={i} className={`relative rounded-xl overflow-hidden border-2 ${isPrimary ? "border-[#FF6B00]" : "border-zinc-200"}`}>
                <img src={u} alt="" className="w-full aspect-square object-cover" onError={(e) => { e.target.style.opacity = 0.3; }} />
                <div className="absolute top-1 right-1 flex gap-1">
                  {!isPrimary && (
                    <button onClick={() => setPrimary(u)} className="text-[10px] font-bold bg-white/95 text-zinc-800 px-2 py-1 rounded-full shadow" data-testid={`img-primary-${i}`}>
                      Set primary
                    </button>
                  )}
                  <button onClick={() => remove(i)} className="text-white bg-red-500 hover:bg-red-600 rounded-full p-1 shadow" data-testid={`img-remove-${i}`}>
                    <X size={10} />
                  </button>
                </div>
                {isPrimary && (
                  <div className="absolute bottom-1 left-1 text-[9px] font-bold bg-[#FF6B00] text-white px-2 py-0.5 rounded-full">PRIMARY</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TiersEditor({ tiers, setTiers }) {
  const add = () => setTiers([...tiers, { quantity: 100, unit_price: 1 }]);
  const remove = (i) => setTiers(tiers.filter((_, idx) => idx !== i));
  const update = (i, k, v) => setTiers(tiers.map((t, idx) => idx === i ? { ...t, [k]: v } : t));
  return (
    <div className="space-y-3">
      <div className="text-sm text-zinc-600">
        Quantity tiers control bulk pricing. When a customer orders ≥ tier quantity, the tier's unit price applies.
      </div>
      <div className="space-y-2" data-testid="tiers-list">
        {tiers.map((t, i) => (
          <div key={i} className="flex items-end gap-2 p-2 border border-zinc-200 rounded-lg" data-testid={`tier-${i}`}>
            <Field label="Quantity ≥">
              <input type="number" className="input w-28" value={t.quantity} onChange={(e) => update(i, "quantity", parseInt(e.target.value) || 0)} />
            </Field>
            <Field label="Unit Price ₹">
              <input type="number" step="0.01" className="input w-28" value={t.unit_price} onChange={(e) => update(i, "unit_price", parseFloat(e.target.value) || 0)} />
            </Field>
            <div className="flex-1 text-xs text-zinc-500">Line: ₹{(t.quantity * t.unit_price).toFixed(2)} @ {t.quantity} units</div>
            <button onClick={() => remove(i)} className="text-red-500 hover:text-red-700 p-2"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      <button onClick={add} className="btn-swiss btn-outline text-xs" data-testid="add-tier"><Plus size={12} /> Add Tier</button>
    </div>
  );
}

function OptionsEditor({ options, setOptions }) {
  const addOpt = () => setOptions([...options, { key: `option_${options.length + 1}`, label: "New Option", type: "select", required: true, values: [] }]);
  const removeOpt = (i) => setOptions(options.filter((_, idx) => idx !== i));
  const updateOpt = (i, k, v) => setOptions(options.map((o, idx) => idx === i ? { ...o, [k]: v } : o));
  const addVal = (i) => updateOpt(i, "values", [...(options[i].values || []), { value: `v_${(options[i].values || []).length + 1}`, label: "New Value", price_delta: 0 }]);
  const updateVal = (i, vi, k, v) => updateOpt(i, "values", options[i].values.map((val, idx) => idx === vi ? { ...val, [k]: v } : val));
  const removeVal = (i, vi) => updateOpt(i, "values", options[i].values.filter((_, idx) => idx !== vi));

  return (
    <div className="space-y-4">
      <div className="text-sm text-zinc-600">
        Customizable options shown to customers (size, paper, colour, lamination etc). Each option value can add a per-unit ₹ delta.
      </div>
      {options.map((opt, i) => (
        <div key={i} className="p-3 border-2 border-zinc-200 rounded-xl" data-testid={`option-${i}`}>
          <div className="grid grid-cols-4 gap-2 mb-3">
            <Field label="Key"><input className="input" value={opt.key} onChange={(e) => updateOpt(i, "key", e.target.value.toLowerCase().replace(/\s+/g, "_"))} /></Field>
            <Field label="Label"><input className="input" value={opt.label} onChange={(e) => updateOpt(i, "label", e.target.value)} /></Field>
            <Field label="Type">
              <select className="input" value={opt.type} onChange={(e) => updateOpt(i, "type", e.target.value)}>
                <option value="select">Select (dropdown)</option>
                <option value="swatch">Swatch (buttons)</option>
                <option value="radio">Radio</option>
              </select>
            </Field>
            <div className="flex items-end justify-end">
              <button onClick={() => removeOpt(i)} className="text-red-500 hover:text-red-700 p-2"><Trash2 size={14} /></button>
            </div>
          </div>
          <div className="space-y-1.5">
            {(opt.values || []).map((v, vi) => (
              <div key={vi} className="grid grid-cols-6 gap-2 items-center bg-zinc-50 p-2 rounded" data-testid={`opt-${i}-val-${vi}`}>
                <input className="input col-span-2" placeholder="value" value={v.value} onChange={(e) => updateVal(i, vi, "value", e.target.value)} />
                <input className="input col-span-2" placeholder="label" value={v.label} onChange={(e) => updateVal(i, vi, "label", e.target.value)} />
                <input type="number" step="0.01" className="input" placeholder="₹ delta" value={v.price_delta || 0} onChange={(e) => updateVal(i, vi, "price_delta", parseFloat(e.target.value) || 0)} />
                <div className="flex items-center gap-1">
                  <label className="text-[10px] font-bold">
                    <input type="checkbox" checked={!!v.is_default} onChange={(e) => updateVal(i, vi, "is_default", e.target.checked)} className="mr-1" />Def
                  </label>
                  <button onClick={() => removeVal(i, vi)} className="text-red-500 hover:text-red-700 ml-auto"><X size={12} /></button>
                </div>
              </div>
            ))}
            <button onClick={() => addVal(i)} className="text-xs font-bold text-[#FF6B00] hover:underline"><Plus size={10} className="inline" /> Add value</button>
          </div>
        </div>
      ))}
      <button onClick={addOpt} className="btn-swiss btn-outline text-xs" data-testid="add-option"><Plus size={12} /> Add Option</button>
    </div>
  );
}

function FAQEditor({ faqs, setFaqs }) {
  const add = () => setFaqs([...faqs, { q: "", a: "" }]);
  const remove = (i) => setFaqs(faqs.filter((_, idx) => idx !== i));
  const upd = (i, k, v) => setFaqs(faqs.map((f, idx) => idx === i ? { ...f, [k]: v } : f));
  return (
    <div className="space-y-3">
      {faqs.map((f, i) => (
        <div key={i} className="p-3 border-2 border-zinc-200 rounded-xl" data-testid={`faq-${i}`}>
          <div className="flex justify-between items-center mb-2">
            <div className="text-xs font-bold text-zinc-500">FAQ #{i + 1}</div>
            <button onClick={() => remove(i)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
          </div>
          <input className="input mb-2" placeholder="Question" value={f.q} onChange={(e) => upd(i, "q", e.target.value)} />
          <textarea className="input min-h-[70px]" placeholder="Answer" value={f.a} onChange={(e) => upd(i, "a", e.target.value)} />
        </div>
      ))}
      <button onClick={add} className="btn-swiss btn-outline text-xs" data-testid="add-faq"><Plus size={12} /> Add FAQ</button>
    </div>
  );
}

function StorageModal({ onClose }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/storage/status");
      setStatus(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load status");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const migrate = async () => {
    setRunning(true);
    let totalMigrated = 0;
    try {
      // Repeatedly call the batch endpoint until all collections show remaining=0
      for (let i = 0; i < 50; i++) {
        const { data } = await api.post("/admin/storage/migrate?batch=50");
        const r = data.report || {};
        const migratedThisRound = Object.values(r).reduce((n, c) => n + (c.migrated_now || 0), 0);
        const remaining = Object.values(r).reduce((n, c) => n + (c.remaining || 0), 0);
        totalMigrated += migratedThisRound;
        if (migratedThisRound === 0 || remaining === 0) break;
      }
      toast.success(`Migrated ${totalMigrated} file(s) to Object Storage`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migration failed");
    } finally { setRunning(false); }
  };

  const totalPending = status ? ((status.media?.pending || 0) + (status.files?.pending || 0) + (status.pod?.pending || 0)) : 0;
  const totalMigrated = status ? ((status.media?.migrated || 0) + (status.files?.migrated || 0) + (status.pod?.migrated || 0)) : 0;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="storage-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Cloud className="text-sky-600" />
            <div className="font-display text-xl font-black tracking-tighter">Object Storage</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-full" data-testid="storage-close"><X size={16} /></button>
        </div>

        {loading && <div className="text-sm text-zinc-500">Loading…</div>}

        {status && (
          <>
            <div className={`px-3 py-2 rounded-xl text-xs font-bold ${status.enabled ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`} data-testid="storage-enabled-badge">
              {status.enabled ? "● Emergent Object Storage connected" : "⚠ Storage not configured"}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-3 text-center" data-testid="storage-migrated-total">
                <div className="text-2xl font-display font-black text-emerald-700">{totalMigrated}</div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-800/70">On Cloud</div>
              </div>
              <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-3 text-center" data-testid="storage-pending-total">
                <div className="text-2xl font-display font-black text-amber-700">{totalPending}</div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-amber-800/70">Pending Migration</div>
              </div>
            </div>

            <table className="w-full text-xs border-2 border-zinc-200 rounded-xl overflow-hidden">
              <thead className="bg-zinc-50 text-zinc-500 uppercase tracking-wider">
                <tr>
                  <th className="p-2 text-left">Collection</th>
                  <th className="p-2 text-right">Migrated</th>
                  <th className="p-2 text-right">Pending</th>
                </tr>
              </thead>
              <tbody>
                {["media", "files", "pod"].map((k) => (
                  <tr key={k} className="border-t border-zinc-100" data-testid={`storage-row-${k}`}>
                    <td className="p-2 font-mono">{k}</td>
                    <td className="p-2 text-right font-bold text-emerald-700">{status[k]?.migrated || 0}</td>
                    <td className="p-2 text-right font-bold text-amber-700">{status[k]?.pending || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <button
              onClick={migrate}
              disabled={running || totalPending === 0 || !status.enabled}
              className="w-full btn-swiss btn-accent text-sm justify-center disabled:opacity-50"
              data-testid="storage-migrate-btn"
            >
              {running ? "Migrating…" : totalPending === 0 ? "All Files Migrated" : `Migrate ${totalPending} File${totalPending > 1 ? "s" : ""} Now`}
            </button>
            <p className="text-[11px] text-zinc-500">
              New uploads go straight to Object Storage. Use this button to move any legacy base64 blobs still in Mongo.
            </p>
          </>
        )}
      </div>
    </div>
  );
}


/**
 * TaxonomyPicker — dropdown of existing groups/categories with a "+ Create new"
 * option that opens an inline input. On submit, POSTs the new name so the
 * taxonomy stays consistent, refreshes the parent list, and selects the value
 * on the product being edited.
 */
function TaxonomyPicker({ kind, value, options, onChange, onCreated, testIdPrefix }) {
  const [mode, setMode] = useState("pick"); // "pick" | "new"
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSelect = (e) => {
    const v = e.target.value;
    if (v === "__NEW__") { setMode("new"); setNewName(""); return; }
    onChange(v);
  };

  const submitNew = async (e) => {
    e?.preventDefault?.();
    const clean = (newName || "").trim();
    if (!clean) return;
    setSaving(true);
    try {
      await api.post(`/admin/catalog/${kind}`, { new_name: clean });
      toast.success(`${kind === "groups" ? "Group" : "Category"} "${clean}" added`);
      onChange(clean);
      if (onCreated) await onCreated();
      setMode("pick");
      setNewName("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  if (mode === "new") {
    return (
      <form onSubmit={submitNew} className="flex gap-2" data-testid={`${testIdPrefix}-new-form`}>
        <input
          autoFocus
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder={`New ${kind === "groups" ? "group" : "category"} name`}
          maxLength={60}
          className="input flex-1"
          data-testid={`${testIdPrefix}-new-input`}
        />
        <button
          type="submit"
          disabled={saving || !newName.trim()}
          className="inline-flex items-center gap-1 bg-[#FF6B00] hover:bg-[#e65a00] disabled:opacity-40 text-white text-xs font-bold px-3 rounded-lg"
          data-testid={`${testIdPrefix}-new-save`}
        >{saving ? "…" : "Add"}</button>
        <button
          type="button"
          onClick={() => { setMode("pick"); setNewName(""); }}
          className="text-xs text-zinc-500 hover:text-zinc-800 px-2"
          data-testid={`${testIdPrefix}-new-cancel`}
        >Cancel</button>
      </form>
    );
  }

  // Ensure the current value appears in the option list even if it's not in `options`
  const opts = value && !options.includes(value) ? [value, ...options] : options;
  return (
    <select
      className="input"
      value={value || ""}
      onChange={handleSelect}
      data-testid={testIdPrefix}
    >
      <option value="" disabled>— Select —</option>
      {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      <option value="__NEW__" style={{ fontWeight: 700, color: "#FF6B00" }}>+ Create new {kind === "groups" ? "group" : "category"}…</option>
    </select>
  );
}


function MultiTaxonomyPicker({ value, primary, options, onChange, testIdPrefix }) {
  // Multi-select checkbox chip picker. Excludes the primary value (already the main
  // group/category) so admins don't accidentally list it twice. Values in `value`
  // that aren't in `options` are still shown so historical picks aren't lost.
  const selected = Array.isArray(value) ? value : [];
  const merged = Array.from(new Set([...(options || []), ...selected])).filter((o) => o && o !== primary);
  const toggle = (opt) => {
    if (selected.includes(opt)) {
      onChange(selected.filter((v) => v !== opt));
    } else {
      onChange([...selected, opt]);
    }
  };
  if (merged.length === 0) {
    return <div className="text-xs text-zinc-500 italic px-1 py-1" data-testid={`${testIdPrefix}-empty`}>No other options — pick a primary first.</div>;
  }
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testIdPrefix}>
      {merged.map((opt) => {
        const on = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className={`text-[11px] font-bold px-2.5 py-1 rounded-full border-2 transition-colors ${on ? "border-[#FF6B00] bg-orange-50 text-[#FF6B00]" : "border-zinc-200 text-zinc-600 hover:border-zinc-400"}`}
            data-testid={`${testIdPrefix}-opt-${opt.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {on ? "✓ " : "+ "}{opt}
          </button>
        );
      })}
    </div>
  );
}

